import React, { useState, useEffect, FunctionComponent, useRef } from 'react'
import { Range } from 'react-date-range'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { TableCell } from '../../../../components/component-items/datatable'
import { Form } from 'react-bootstrap'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import Select from 'react-select'
import { MultiSelect } from '../../schedules/schedule/styles'
import AsyncSelect from 'react-select/async'
import { CustomDateInput } from '../../../../components/component-items/custom-date-input'

// Types
import {
  FiltersProps,
  ConditionProps,
  defaultFilters,
  OrderFieldTypes,
  AddressFieldTypes,
  orderItemFieldTypes,
  BoolFieldTypes,
  StringFieldTypes,
  NumberFieldTypes,
  DateFieldTypes,
  AllFieldTypes,
  ASNFieldTypes,
  ReturnFieldTypes,
  OrderConditionOptions,
  ASNConditionOptions,
  ReturnConditionOptions,
  InventoryConditionOptions,
  ProductVariantConditionOptions,
  BillingConditionOptions,
  InventoryFieldTypes,
  ProductVariantFieldTypes,
  BillingFieldTypes,
  ListFieldTypes,
} from './types'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt, faLayerPlus } from '@fortawesome/pro-duotone-svg-icons'

export const Condition: FunctionComponent<ConditionProps> = ({
  automationType,
  filters,
  setFilters,
  children,
  r,
  g,
  b,
}) => {
  const [logical, setLogical] = useState(filters.logical)
  useEffect(() => {
    setFilters({ ...filters, logical: logical })
  }, [logical])

  function addRow() {
    setLogical((logical: FiltersProps[]) => [...logical, defaultFilters])
  }

  const setType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'logical_not') {
      setLogical([defaultFilters])
    } else {
      setLogical([defaultFilters, defaultFilters])
    }
    setFilters({ ...defaultFilters, type: e.target.value })
  }

  const isLogical = ['logical_and', 'logical_or', 'logical_not'].includes(filters.type)
  const noConditions = [
    'all_orders',
    'all_asns',
    'all_returns',
    'all_inventory_items',
    'all_product_variants',
    'all_billing_transactions',
  ].includes(filters.type)
  const isOrderItem = ['order_item_any', 'order_item_all', 'order_item_none'].includes(filters.type)

  // Instantiate Field Types
  const orderFieldList = OrderFieldTypes()
  const orderItemFieldList = orderItemFieldTypes()
  const addressFieldList = AddressFieldTypes()
  const asnFieldList = ASNFieldTypes()
  const returnFieldList = ReturnFieldTypes()
  const inventoryFieldList = InventoryFieldTypes()
  const productVariantFieldList = ProductVariantFieldTypes()
  const billingFieldList = BillingFieldTypes()

  // Set Field List, Field Operator, Field Options, and Field Type
  let fieldList: any = []
  if (automationType === 'order') {
    if (filters.type === 'order_field') {
      fieldList = [...orderFieldList]
    } else if (filters.type === 'address_field') {
      fieldList = [...addressFieldList]
    } else if (isOrderItem) {
      fieldList = [...orderItemFieldList]
    }
  } else if (automationType === 'asn') {
    fieldList = [...asnFieldList]
  } else if (automationType === 'return') {
    fieldList = [...returnFieldList]
  } else if (automationType === 'inventory') {
    fieldList = [...inventoryFieldList]
  } else if (automationType === 'product_variant') {
    fieldList = [...productVariantFieldList]
  } else if (automationType === 'billing') {
    fieldList = [...billingFieldList]
  }

  const fieldOperator: any = fieldList.find((t: any) => t.value === filters.result.fieldType)
  const fieldOptions: any = fieldOperator?.options
  const fieldType: any = fieldOperator?.type
  const isFieldOptionsArray = Array.isArray(fieldOptions)
  const fieldOptionsArray = isFieldOptionsArray ? fieldOptions : []
  const isMulti = filters.result.fieldOperator === 'in' && fieldOptionsArray.length > 1
  const booleanFieldValue =
    filters.result.fieldValue === true ||
    filters.result.fieldValue === 'true' ||
    filters.result.fieldValue === 1 ||
    filters.result.fieldValue === '1'

  useEffect(() => {
    const hasValue =
      filters.result.fieldValue !== undefined && filters.result.fieldValue !== null && filters.result.fieldValue !== ''
    let fieldValue: any = filters.result.fieldValue

    if (fieldType === 'boolean') {
      if (fieldValue === 'true' || fieldValue === '1') {
        fieldValue = true
      } else if (fieldValue === 'false' || fieldValue === '0') {
        fieldValue = false
      } else if (!hasValue) {
        fieldValue = false
      }
    } else if (fieldType === 'date' && !hasValue) {
      fieldValue = new Date()
    } else if (!hasValue) {
      fieldValue = ''
    }

    if (fieldValue !== filters.result.fieldValue) {
      setFilters({
        ...filters,
        result: { ...filters.result, fieldValue: fieldValue },
      })
    }
  }, [fieldType])

  const selectValue =
    fieldOptionsArray.length && isMulti && Array.isArray(filters.result.fieldValue)
      ? fieldOptionsArray.filter((option: any) =>
          filters.result.fieldValue.some((value: any) => `${value}` === `${option.value}`)
        )
      : fieldOptionsArray.length
        ? fieldOptionsArray.find((option: any) => `${option.value}` === `${filters.result.fieldValue}`)
        : null

  const [asyncFieldOptions, setAsyncFieldOptions]: any = useState([])
  useEffect(() => {
    // Load async options and set them when they are ready
    const loadOptionsAsync = async () => {
      const options = typeof fieldOptions === 'function' ? await fieldOptions() : []
      setAsyncFieldOptions(options)
    }

    if (fieldOperator?.async) {
      loadOptionsAsync()
    }
  }, [])

  // Helper function to parse DD/MM/YYYY format
  const parseDateFromBackend = (dateStr: string | Date): Date | null => {
    if (!dateStr) return null

    // If already a Date object, return it
    if (dateStr instanceof Date) {
      return isNaN(dateStr.getTime()) ? null : dateStr
    }

    // Handle canonical YYYY-MM-DD format as local date (avoid UTC shift)
    const yyyymmddMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (yyyymmddMatch) {
      const [, year, month, day] = yyyymmddMatch
      return new Date(Number(year), Number(month) - 1, Number(day))
    }

    // Handle DD/MM/YYYY format from backend
    const parts = String(dateStr).split('/')
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
      const year = parseInt(parts[2], 10)

      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month, day)
      }
    }

    // Fallback to native Date parsing
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? null : date
  }

  const handleDateChange = (newDate: Date | Range | null) => {
    if (newDate instanceof Date) {
      // Format date as DD/MM/YYYY for backend compatibility
      const day = String(newDate.getDate()).padStart(2, '0')
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const year = newDate.getFullYear()
      const formattedDate = `${day}/${month}/${year}`

      setFilters({
        ...filters,
        result: { ...filters.result, fieldValue: formattedDate },
      })
    }
  }

  return (
    <GlobalStyles.DataTable>
      <tbody>
        <tr style={{ border: 'unset' }}>
          <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
            <Form.Select required value={filters.type} onChange={(e) => setType(e)}>
              {automationType === 'order' ? (
                <OrderConditionOptions />
              ) : automationType === 'asn' ? (
                <ASNConditionOptions />
              ) : automationType === 'return' ? (
                <ReturnConditionOptions />
              ) : automationType === 'inventory' ? (
                <InventoryConditionOptions />
              ) : automationType === 'product_variant' ? (
                <ProductVariantConditionOptions />
              ) : automationType === 'billing' ? (
                <BillingConditionOptions />
              ) : null}
            </Form.Select>
            {isLogical ? (
              <GlobalStyles.DataTable>
                <tbody>
                  <tr style={{ border: 'unset' }}>
                    <TableCell style={{ background: `rgb(${r}, ${g}, ${b})`, border: '1px solid #e1eaf1' }}>
                      {logical.map((newFilters: FiltersProps, index: number) => (
                        <React.Fragment key={index}>
                          <Condition
                            automationType={automationType}
                            filters={newFilters}
                            setFilters={(e: any) =>
                              setLogical((logical: FiltersProps[]) => [
                                ...logical.slice(0, index),
                                { ...e },
                                ...logical.slice(index + 1),
                              ])
                            }
                            r={r * 0.95}
                            g={g * 0.95}
                            b={b * 0.95}
                          >
                            {filters.type !== 'logical_not' ? (
                              <TableCell
                                center
                                style={{ verticalAlign: 'unset', width: 50, background: 'rgb(0,0,0,0)' }}
                                input={
                                  <div className="dropdown__container" style={{ minWidth: 60 }}>
                                    {logical.length > 2 ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setLogical((logical: FiltersProps[]) =>
                                            logical.filter((c) => c !== newFilters)
                                          )
                                        }
                                      >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                      </button>
                                    ) : null}
                                    <button type="button" onClick={() => addRow()}>
                                      <FontAwesomeIcon icon={faLayerPlus} />
                                    </button>
                                  </div>
                                }
                              />
                            ) : null}
                          </Condition>
                          {index + 1 !== logical.length ? (
                            <StatusBoxStyled
                              className={'blue'}
                              style={{ margin: '1em auto', width: 100, textAlign: 'center' }}
                            >
                              {filters.type === 'logical_and' ? 'AND' : 'OR'}
                            </StatusBoxStyled>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </TableCell>
                  </tr>
                </tbody>
              </GlobalStyles.DataTable>
            ) : null}
          </TableCell>
          {!isLogical && !noConditions ? (
            <>
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={filters.result.fieldType}
                  onChange={(e) => setFilters({ ...filters, result: { ...filters.result, fieldType: e.target.value } })}
                >
                  <option value="" disabled hidden>
                    - Field Type -
                  </option>
                  {fieldList?.map(({ value, label }: any) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </TableCell>
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={filters.result.fieldOperator}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      result: { ...filters.result, fieldOperator: e.target.value },
                    })
                  }
                >
                  <option value={''} disabled hidden>
                    - Field Operator -
                  </option>
                  {fieldOperator?.type === 'boolean'
                    ? BoolFieldTypes.map(({ value, label }: any) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))
                    : fieldOperator?.type === 'string'
                      ? StringFieldTypes.map(({ value, label }: any) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))
                      : fieldOperator?.type === 'number'
                        ? NumberFieldTypes.map(({ value, label }: any) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))
                        : fieldOperator?.type === 'date'
                          ? DateFieldTypes.map(({ value, label }: any) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))
                          : fieldOperator?.type === 'list'
                            ? ListFieldTypes.map(({ value, label }: { value: string; label: string }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))
                            : AllFieldTypes.map(({ value, label }: any) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                </Form.Select>
              </TableCell>
              {fieldOperator?.async ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <MultiSelect>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={true}
                      loadOptions={fieldOptions}
                      value={asyncFieldOptions.find(({ value }: any) => value === filters.result.fieldValue)}
                      onChange={(e: any) =>
                        setFilters({
                          ...filters,
                          result: { ...filters.result, fieldValue: e.value },
                        })
                      }
                      closeMenuOnSelect={false}
                      className="basic-multi-select"
                    />
                  </MultiSelect>
                </TableCell>
              ) : fieldOptionsArray.length ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)', minWidth: 200 }}>
                  <MultiSelect>
                    <Select
                      options={fieldOptionsArray}
                      value={selectValue}
                      onChange={(e: any) =>
                        isMulti
                          ? setFilters({
                              ...filters,
                              result: { ...filters.result, fieldValue: e.map((o: any) => o.value) },
                            })
                          : setFilters({
                              ...filters,
                              result: { ...filters.result, fieldValue: e.value },
                            })
                      }
                      closeMenuOnSelect={!isMulti}
                      isMulti={isMulti}
                      className={isMulti ? 'basic-multi-select' : ''}
                    />
                  </MultiSelect>
                </TableCell>
              ) : fieldOperator?.type === 'boolean' ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Select
                    options={[
                      { label: 'True', value: true },
                      { label: 'False', value: false },
                    ]}
                    value={booleanFieldValue ? { label: 'True', value: true } : { label: 'False', value: false }}
                    onChange={(e: any) =>
                      setFilters({
                        ...filters,
                        result: { ...filters.result, fieldValue: e.value },
                      })
                    }
                  />
                </TableCell>
              ) : fieldType === 'date' ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <CustomDateInput
                    value={filters.result.fieldValue ? parseDateFromBackend(filters.result.fieldValue) : null}
                    onChange={handleDateChange}
                    required={true}
                    placeholder="MM-DD-YYYY"
                  />
                </TableCell>
              ) : !['is_blank', 'is_not_blank'].includes(filters.result.fieldOperator) ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Form.Control
                    type="text"
                    required
                    placeholder=""
                    value={filters.result.fieldValue}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        result: { ...filters.result, fieldValue: e.target.value },
                      })
                    }
                  />
                </TableCell>
              ) : null}
            </>
          ) : null}
          {children}
        </tr>
      </tbody>
    </GlobalStyles.DataTable>
  )
}
